/**
 * Utility functions for image cropping and processing
 */

/**
 * Auto-crops an image by removing white (#FFFFFF) margins from all sides
 * while maintaining the aspect ratio constraint
 * 
 * @param imageBase64 Base64 encoded image data
 * @returns Promise resolving to the cropped image as base64 string
 */
export const autoCropWhiteMargins = (imageBase64: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      
      img.onload = () => {
        // Create a canvas to analyze the image
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0);
        
        // Get image data to analyze pixels
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Find the boundaries of non-white content
        let minX = canvas.width;
        let minY = canvas.height;
        let maxX = 0;
        let maxY = 0;
        
        // Scan through all pixels to find the boundaries
        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const idx = (y * canvas.width + x) * 4;
            
            // Check if pixel is not white (allowing for slight variations)
            // R, G, B values are at idx, idx+1, idx+2, and alpha is at idx+3
            const isWhite = 
              data[idx] >= 250 && 
              data[idx + 1] >= 250 && 
              data[idx + 2] >= 250 && 
              data[idx + 3] >= 250;
            
            if (!isWhite) {
              minX = Math.min(minX, x);
              minY = Math.min(minY, y);
              maxX = Math.max(maxX, x);
              maxY = Math.max(maxY, y);
            }
          }
        }
        
        // Add a small padding (5px) around the content
        const padding = 5;
        minX = Math.max(0, minX - padding);
        minY = Math.max(0, minY - padding);
        maxX = Math.min(canvas.width - 1, maxX + padding);
        maxY = Math.min(canvas.height - 1, maxY + padding);
        
        // Calculate dimensions of the cropped image
        const width = maxX - minX + 1;
        const height = maxY - minY + 1;
        
        // Check if we found any non-white content
        if (width <= 0 || height <= 0 || width === canvas.width || height === canvas.height) {
          // No cropping needed or no content found
          resolve(imageBase64);
          return;
        }
        
        // Create a new canvas for the cropped image
        const croppedCanvas = document.createElement('canvas');
        croppedCanvas.width = width;
        croppedCanvas.height = height;
        
        const croppedCtx = croppedCanvas.getContext('2d');
        if (!croppedCtx) {
          reject(new Error('Failed to get cropped canvas context'));
          return;
        }
        
        // Draw the cropped portion onto the new canvas
        croppedCtx.drawImage(
          canvas, 
          minX, minY, width, height,
          0, 0, width, height
        );
        
        // Convert the cropped canvas to base64
        const croppedImageBase64 = croppedCanvas.toDataURL('image/png');
        resolve(croppedImageBase64);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for auto-cropping'));
      };
      
      img.src = imageBase64;
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Represents a bounding box for cropping.
 * Coordinates are relative to the source image.
 */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Crops an image to a specified bounding box.
 * @param imageBase64 Base64 encoded image data.
 * @param boundingBox The area to crop to, relative to the image dimensions.
 * @returns Promise resolving to the cropped image as base64 string.
 */
export const cropToBoundingBox = (imageBase64: string, boundingBox: BoundingBox): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      // Ensure bounding box coordinates are within image dimensions and positive
      const cropX = Math.max(0, boundingBox.x);
      const cropY = Math.max(0, boundingBox.y);
      const cropWidth = Math.max(0, Math.min(boundingBox.width, img.width - cropX));
      const cropHeight = Math.max(0, Math.min(boundingBox.height, img.height - cropY));

      if (cropWidth <= 0 || cropHeight <= 0) {
        // Bounding box is invalid or outside image, return original or handle error
        console.warn("Bounding box for cropping is invalid or outside image dimensions.");
        resolve(imageBase64); // Or reject with an error
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context for bounding box crop'));
        return;
      }

      // Draw the specified portion of the original image onto the new canvas
      ctx.drawImage(
        img,
        cropX, // Source X
        cropY, // Source Y
        cropWidth, // Source Width
        cropHeight, // Source Height
        0, // Destination X
        0, // Destination Y
        cropWidth, // Destination Width
        cropHeight // Destination Height
      );

      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = (err) => {
      console.error('Failed to load image for bounding box cropping:', err);
      reject(new Error('Failed to load image for bounding box cropping'));
    };

    img.src = imageBase64;
  });
};

/**
 * Pre-scales an image to fit within target dimensions while maintaining aspect ratio.
 * Uses canvas for potentially better resampling quality.
 * @param imageBase64 Base64 encoded image data.
 * @param targetWidthPx The target maximum width for the pre-scaled image.
 * @param targetHeightPx The target maximum height for the pre-scaled image.
 * @returns Promise resolving to the pre-scaled image as a base64 string.
 */
export const preScaleImage = (
  imageBase64: string,
  targetWidthPx: number,
  targetHeightPx: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const originalWidth = img.width;
      const originalHeight = img.height;

      // If the image is already smaller than the target, no need to scale up or process.
      // Or if it's only slightly larger, the benefit might be minimal.
      // For now, we'll scale if it's larger in any dimension.
      if (originalWidth <= targetWidthPx && originalHeight <= targetHeightPx) {
        // resolve(imageBase64); // Option: return original if already small enough
        // For consistency and to ensure it goes through canvas, let's still process it,
        // but it will be drawn at its original size if smaller.
      }

      let scaledWidth = originalWidth;
      let scaledHeight = originalHeight;

      // Calculate new dimensions while maintaining aspect ratio
      if (originalWidth > targetWidthPx) {
        scaledWidth = targetWidthPx;
        scaledHeight = (originalHeight * targetWidthPx) / originalWidth;
      }

      if (scaledHeight > targetHeightPx) {
        scaledHeight = targetHeightPx;
        scaledWidth = (originalWidth * targetHeightPx) / originalHeight;
      }
      
      // Ensure dimensions are integers
      scaledWidth = Math.round(scaledWidth);
      scaledHeight = Math.round(scaledHeight);

      if (scaledWidth <= 0 || scaledHeight <= 0) {
        console.warn("Pre-scaling resulted in zero or negative dimensions.");
        resolve(imageBase64); // Return original if scaling is problematic
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = scaledWidth;
      canvas.height = scaledHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context for pre-scaling'));
        return;
      }

      // Draw the original image onto the new canvas, scaled down
      ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = (err) => {
      console.error('Failed to load image for pre-scaling:', err);
      reject(new Error('Failed to load image for pre-scaling'));
    };

    img.src = imageBase64;
  });
};
